import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Grid,
  Slide,
  Fade,
  Divider,
  Container,
} from "@mui/material";
import {
  AccountBalanceWallet,
  Bolt,
  CheckCircle,
  EventAvailable,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { getHeader, toastError, validatePaymentAmount } from "../../utils/util";
import { ZOHO_ACCOUNT_ID, ZOHO_API_KEY } from "./PaymentWidget";
import { API_ENDPOINT } from "../../utils/constants";
import axios from "axios";
import SuccessPopup from "./SuccessPopup";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackBtn from "../../widgets/BackBtn";

const GST_RATE = 0.18; // 18% GST
const MIN_RECHARGE = 5; // kept consistent with existing validation call
const MIN_BASE_AMOUNT = 2000; // backend enforces baseAmount (excl. GST) >= 2000

// Validate & normalize the query params for a "validity" payment.
// Returns null if the params don't describe a valid validity request,
// in which case the component falls back to the normal balance recharge flow.
function parseValidityParams(searchParams) {
  const type = searchParams.get("type") || "balance";
  if (type !== "validity") return null;

  const rawAmount = searchParams.get("amount");
  const rawPeriod = searchParams.get("period");

  const amount = Number(rawAmount);
  const period = Number(rawPeriod);

  const isAmountValid =
    rawAmount !== null &&
    rawAmount !== "" &&
    Number.isFinite(amount) &&
    amount > 0;
  const isPeriodValid =
    rawPeriod !== null &&
    rawPeriod !== "" &&
    Number.isInteger(period) &&
    period >= 1 &&
    period <= 12;

  if (!isAmountValid || !isPeriodValid) {
    return { invalid: true };
  }

  return { invalid: false, amount, period };
}

const RechargeComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const validity = useMemo(
    () => parseValidityParams(searchParams),
    [searchParams]
  );
  const isValidityMode = !!validity && !validity.invalid;
  const isValidityParamsInvalid = !!validity && validity.invalid;

  const [amount, setAmount] = useState(
    isValidityMode ? validity.amount.toString() : ""
  );
  const [selectedOption, setSelectedOption] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Updated preset amounts (minimum 2000)
  const presetAmounts = [2000, 10000, 50000];

  // Calculate GST and total amount
  const baseAmount = isValidityMode ? validity.amount : parseFloat(amount) || 0;
  const period = isValidityMode ? validity.period : null;
  const gstAmount = baseAmount * GST_RATE;
  const totalAmount = baseAmount + gstAmount;

  const handleRecharge = useCallback(async () => {
    if (isValidityMode) {
      if (baseAmount < MIN_BASE_AMOUNT) {
        toast.error(
          `Amount excluding GST must be at least ₹${MIN_BASE_AMOUNT}`
        );
        return;
      }
    } else {
      const validationResult = validatePaymentAmount(
        baseAmount,
        MIN_RECHARGE,
        999999999
      );
      if (!validationResult.isValid) {
        toast.error(validationResult.errorMessage);
        return;
      }
    }

    let instance = new window.ZPayments({
      account_id: ZOHO_ACCOUNT_ID,
      domain: "IN",
      otherOptions: {
        api_key: ZOHO_API_KEY,
      },
    });

    try {
      setIsProcessing(true);

      const paymentType = isValidityMode ? "validity" : "balance";
      const sessionUrl = `${API_ENDPOINT}/payment/create-zoho-payment-session?accountId=${ZOHO_ACCOUNT_ID}&type=${paymentType}`;

      const response = await axios.post(
        sessionUrl,
        {
          amount: totalAmount.toFixed(2), // Send total amount including GST
          ...(isValidityMode ? { period } : {}),
        },
        getHeader()
      );
      const paymentSession = response.data.payments_session;
      const sessionAccountInfo = response.data.accountInfo;
      let reference_number = `${sessionAccountInfo.id}-${Date.now()}`;

      if (reference_number.length > 50) {
        reference_number = reference_number.slice(0, 50);
      }
      let options = {
        amount: totalAmount.toFixed(2), // Use total amount
        currency_code: "INR",
        payments_session_id: paymentSession.payments_session_id,
        currency_symbol: "₹",
        business: sessionAccountInfo.id,
        description: isValidityMode
          ? `Validity extension of ${period} month(s) for ${
              sessionAccountInfo.verifiedName
            } (Base: ₹${baseAmount.toFixed(2)}, GST: ₹${gstAmount.toFixed(2)})`
          : `Purchase of ${
              sessionAccountInfo.verifiedName
            } (Base: ₹${baseAmount.toFixed(2)}, GST: ₹${gstAmount.toFixed(2)})`,
        invoice_number: paymentSession.invoice_number,
        reference_number: reference_number,
        address: {
          name: sessionAccountInfo.verifiedName,
          email: sessionAccountInfo.email,
          phone: sessionAccountInfo.phoneId,
        },
      };
      let data = await instance.requestPaymentMethod(options);
      if (data && data.payment_id) {
        setPaymentResponse({
          ...data,
          baseAmount: baseAmount.toFixed(2),
          gstAmount: gstAmount.toFixed(2),
          totalAmount: totalAmount.toFixed(2),
          ...(isValidityMode ? { period } : {}),
        }); // Store base, GST, and total amounts
        setShowSuccess(true);
        setIsSuccess(true);
      }
    } catch (error) {
      console.log(error);
      if (error.code !== "widget_closed") {
        toastError(error);
      }
    } finally {
      setIsProcessing(false);
      await instance.close();
      document.location.reload();
    }
  }, [amount, totalAmount, isValidityMode, period, baseAmount, gstAmount]);

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate(`/home`);
  };

  const canPay = isValidityMode
    ? baseAmount >= MIN_BASE_AMOUNT
    : baseAmount >= MIN_RECHARGE;

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <BackBtn />
      <Box
        sx={{
          width: "100%",
          p: { xs: 2, md: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 4,
          background: `
        linear-gradient(180deg,
          rgba(248, 250, 252, 0.9) 0%,
          rgba(241, 245, 249, 0.95) 30%,
          rgba(255, 255, 255, 1) 100%
        ),
        radial-gradient(
          circle at 80% 20%,
          rgba(59, 130, 246, 0.08) 0%,
          transparent 60%
        )
      `,
          minHeight: "100vh",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "300px",
            background: `
          radial-gradient(
            ellipse at top center,
            rgba(59, 130, 246, 0.05) 0%,
            transparent 70%
          )
        `,
            zIndex: 0,
          },
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 4,
            background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.03)",
            maxWidth: 700,
            width: "100%",
            mx: "auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            {isValidityMode ? (
              <EventAvailable
                sx={{
                  fontSize: 48,
                  color: "#6366F1",
                  mb: 1,
                  filter: "drop-shadow(0 0 8px rgba(99, 102, 241, 0.2))",
                }}
              />
            ) : (
              <AccountBalanceWallet
                sx={{
                  fontSize: 48,
                  color: "#6366F1",
                  mb: 1,
                  filter: "drop-shadow(0 0 8px rgba(99, 102, 241, 0.2))",
                }}
              />
            )}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1F2937",
                mb: 1,
              }}
            >
              {isValidityMode ? "Extend Validity" : "Account Recharge"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(75,85,99,0.8)" }}>
              {isValidityMode
                ? `Extend your plan validity by ${period} month${
                    period === 1 ? "" : "s"
                  }`
                : "Add credits to your wallet instantly (Minimum ₹2000)"}
            </Typography>
          </Box>

          {isValidityParamsInvalid && (
            <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
              This payment link is invalid or has expired. Please go back and
              try again.
            </Typography>
          )}

          {!isValidityParamsInvalid && !isSuccess ? (
            <>
              {!isValidityMode && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "rgba(75,85,99,0.9)",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: 600,
                    }}
                  >
                    <Bolt sx={{ color: "#F59E0B", fontSize: 18 }} />
                    Select Amount
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {presetAmounts.map((value) => (
                      <Grid item xs={6} sm={3} key={value}>
                        <Button
                          fullWidth
                          variant={
                            selectedOption === value ? "contained" : "outlined"
                          }
                          sx={{
                            py: 2,
                            borderRadius: 2,
                            borderColor:
                              selectedOption === value
                                ? "transparent"
                                : "rgba(0,0,0,0.1)",
                            background:
                              selectedOption === value
                                ? "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)"
                                : "rgba(255,255,255,0.8)",
                            color:
                              selectedOption === value
                                ? "#FFF"
                                : "rgba(75,85,99,0.9)",
                            fontWeight: 600,
                            transition: "all 0.3s ease",
                            borderWidth: selectedOption === value ? 0 : 1,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow:
                                selectedOption === value
                                  ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                                  : "0 4px 8px rgba(0,0,0,0.1)",
                              background:
                                selectedOption === value
                                  ? "linear-gradient(90deg, #5B5FDE 0%, #7C4DFF 100%)"
                                  : "rgba(255,255,255,1)",
                            },
                          }}
                          onClick={() => {
                            setSelectedOption(value);
                            setAmount(value.toString());
                          }}
                        >
                          ₹{value}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                  <TextField
                    fullWidth
                    label="Custom Amount"
                    variant="outlined"
                    type="number"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setSelectedOption(null);
                    }}
                    helperText="Minimum recharge amount is ₹2000"
                    FormHelperTextProps={{
                      sx: { color: "rgba(75,85,99,0.7)" },
                    }}
                    InputProps={{
                      sx: {
                        color: "#1F2937",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0,0,0,0.1)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0,0,0,0.2)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366F1",
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        color: "rgba(75,85,99,0.7)",
                        "&.Mui-focused": {
                          color: "#6366F1",
                        },
                      },
                    }}
                    sx={{ mb: 3 }}
                  />

                  {/* Error if amount is entered but below minimum */}
                  {amount && baseAmount > 0 && baseAmount < MIN_RECHARGE && (
                    <Typography color="error" sx={{ mb: 2 }}>
                      Minimum recharge amount is ₹{MIN_RECHARGE}
                    </Typography>
                  )}
                </>
              )}

              {/* Display Amount Breakdown only if valid */}
              {baseAmount >= (isValidityMode ? 0 : MIN_RECHARGE) && (
                <Box sx={{ mb: 3, color: "rgba(75,85,99,0.9)" }}>
                  <Divider sx={{ mb: 2, borderColor: "rgba(0,0,0,0.06)" }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Amount Breakdown
                  </Typography>
                  {isValidityMode && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2">Validity Period:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {period} month{period === 1 ? "" : "s"}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Base Amount:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      ₹{baseAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">GST (18%):</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      ₹{gstAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: 600,
                    }}
                  >
                    <Typography variant="body2">Total Amount:</Typography>
                    <Typography variant="body2" color="#1F2937">
                      ₹{totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  {isValidityMode && baseAmount < MIN_BASE_AMOUNT && (
                    <Typography color="error" sx={{ mt: 1.5 }}>
                      Amount excluding GST must be at least ₹{MIN_BASE_AMOUNT}
                    </Typography>
                  )}
                </Box>
              )}

              <Slide direction="up" in={canPay} mountOnEnter unmountOnExit>
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isProcessing || !canPay}
                    onClick={handleRecharge}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background:
                        "linear-gradient(90deg, #F59E0B 0%, #D97706 100%)",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                        background:
                          "linear-gradient(90deg, #EAB308 0%, #B45309 100%)",
                        "&::after": {
                          left: "100%",
                        },
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.7s ease",
                      },
                      "&.Mui-disabled": {
                        background: "rgba(0,0,0,0.1)",
                        color: "rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    {isProcessing
                      ? "Processing..."
                      : `Pay Now ₹${totalAmount.toFixed(2)}`}
                  </Button>
                </Box>
              </Slide>
            </>
          ) : null}

          {!isValidityParamsInvalid && isSuccess && (
            <Fade in={isSuccess}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  color: "#059669",
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    filter: "drop-shadow(0 0 8px rgba(5, 150, 105, 0.2))",
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {isValidityMode
                    ? "Validity Extended Successfully!"
                    : "Recharge Successful!"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, color: "#1F2937" }}>
                  {isValidityMode
                    ? `Your validity has been extended by ${period} month${
                        period === 1 ? "" : "s"
                      }`
                    : `₹${totalAmount.toFixed(
                        2
                      )} has been added to your account`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(75,85,99,0.8)" }}
                >
                  (Base: ₹{baseAmount.toFixed(2)}, GST: ₹{gstAmount.toFixed(2)})
                </Typography>
              </Box>
            </Fade>
          )}

          <SuccessPopup
            open={showSuccess}
            amount={totalAmount.toFixed(2)} // Pass total amount
            paymentResponse={paymentResponse}
            onClose={handleSuccessClose}
          />
        </Card>
      </Box>
    </Container>
  );
};

export default RechargeComponent;
